import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from '../../../components/ui/dialog';
import EditorModalShell, { EditorBarFrame, EditorBarTools, EditorBarPrimary } from '../../../components/common/EditorModalShell';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from '../../../components/ui/dropdown-menu';
import {
    IconDownload,
    IconMinus,
    IconRotateClockwise,
    IconFlipHorizontal,
    IconChevronLeft,
    IconChevronRight,
    IconChevronDown,
    IconChevronUp,
    IconCrop,
    IconPlus,
    IconId,
    IconUsers,
    IconNfc,
    IconPlaneDeparture,
    IconCertificate,
    IconPhoto,
    IconFileText,
    IconBorderOuter,
} from '@tabler/icons-react';

const SIZE_ICONS = {
    passport:  IconId,
    dual:      IconUsers,
    epass:     IconNfc,
    visa:      IconPlaneDeparture,
    birth:     IconCertificate,
    print_2r:  IconPhoto,
    print_3r:  IconPhoto,
    print_4r:  IconPhoto,
    print_a4:  IconFileText,
    custom:    IconPlus,
};
import { notifications } from '../../../lib/notifications';
import imageGenerationService from '../../../services/imageGenerationService';
import { VISA_GROUPS } from './VisaCountryModal';
import DownloadOptionsDock from './DownloadOptionsDock';
import { parseBgToken, paintSpecialBg, drawImageCover, presetSrc } from './bgPresets';

// ── Background color resolution (mirrors GenerateImageManual BG_HEX) ─────────
const BG_HEX = {
    white:     '#ffffff',
    blue:      '#2196F3',
    sky_blue:  '#03A9F4',
    grey:      '#9E9E9E',
    dark_blue: '#3068E1',
    teal:      '#00897B',
    off_white: '#FFF8DC',
};

function resolveBgColor(bgKey) {
    if (!bgKey || bgKey === 'transparent') return null; // null = keep canvas transparent
    if (bgKey.startsWith('#')) return bgKey;            // custom hex
    return BG_HEX[bgKey] ?? '#ffffff';                 // named key or fallback
}

// ── Photo size presets (exportW/exportH at 300 DPI) ─────────────────────────
// singleOnly: hidden for dual images  |  isDual: only shown for dual images
const PHOTO_SIZES = [
    { key: 'passport', labelKey: 'abbrPassport', exportW: 532,  exportH: 650,  wmm: 45,    hmm: 55,    singleOnly: true },
    { key: 'dual',     labelKey: 'abbrDual',     exportW: 1200, exportH: 600,  wmm: 101.6, hmm: 50.8,  isDual: true },
    { key: 'epass',    labelKey: 'abbrEPass',    exportW: 413,  exportH: 531,  wmm: 35,    hmm: 45,    singleOnly: true },
    { key: 'visa',     labelKey: 'abbrVisa',     exportW: null, exportH: null, isVisa: true,            singleOnly: true },
    { key: 'birth',    labelKey: 'abbrBirth',    exportW: 413,  exportH: 531,  wmm: 35,    hmm: 45,    singleOnly: true },
    { key: 'print_2r', labelKey: 'abbr2R',       exportW: 750,  exportH: 1050, wmm: 63.5,  hmm: 88.9  },
    { key: 'print_3r', labelKey: 'abbr3R',       exportW: 1050, exportH: 1500, wmm: 88.9,  hmm: 127   },
    { key: 'print_4r', labelKey: 'abbr4R',       exportW: 1200, exportH: 1800, wmm: 101.6, hmm: 152.4 },
    { key: 'print_a4', labelKey: 'abbrA4',       exportW: 2480, exportH: 3508, wmm: 210,   hmm: 297   },
];

const QUALITY_PRESETS = [
    { key: 'low',  value: 75  },
    { key: 'med',  value: 88  },
    { key: 'high', value: 95  },
];

const CROP_RATIOS = [
    { labelKey: 'cropFree', value: null },
    { label: '1:1',  value: 1 },
    { label: '4:3',  value: 4 / 3 },
    { label: '3:4',  value: 3 / 4 },
    { label: '3:2',  value: 3 / 2 },
    { label: '2:3',  value: 2 / 3 },
    { label: '16:9', value: 16 / 9 },
    { label: '9:16', value: 9 / 16 },
];

// Quick border-colour swatches (custom colour is available via the picker input)
const BORDER_SWATCHES = ['#ffffff', '#000000', '#E68A00', '#2196F3', '#e11d48', '#16a34a'];

// Unit conversion at 300 DPI for print sizes
const MM_TO_PX = 300 / 25.4;
const UNIT_TO_PX = { px: 1, mm: MM_TO_PX, cm: MM_TO_PX * 10, in: 300 };

// Parse visa group size string → mm. Handles both units: "35×45 mm" and
// "2×2 inch" (inches were previously read as mm → a 2×2" US visa exported at a
// tiny 24px instead of 600px).
function parseVisaSize(group) {
    const m = group.size.match(/(\d+(?:\.\d+)?)×(\d+(?:\.\d+)?)/);
    if (!m) return null;
    const toMm = /in(ch)?/i.test(group.size) ? 25.4 : 1;
    return { w: parseFloat(m[1]) * toMm, h: parseFloat(m[2]) * toMm };
}

// `getBlob` lets callers whose images live outside imageGenerationService
// (e.g. bulk-photo projects) supply their own proxy fetcher. `inline` renders it in the page's own space
// (the phone's result screen) instead of a full-screen window: the phone layout, without the shell's logo.
export default function CustomDownloadModal({ opened, onClose, imageUrl, imageId, bgKey, images = [], getBlob = null, inline = false }) {
    const { t } = useTranslation();
    const [isMobile, setIsMobile] = useState(() => inline || window.innerWidth < 768);
    // 768–1023px: a narrower, tighter sidebar with fewer options per row, so the photo keeps the room
    const [narrow, setNarrow] = useState(() => window.innerWidth >= 768 && window.innerWidth < 1024);
    useEffect(() => {
        const handler = () => { setIsMobile(inline || window.innerWidth < 768); setNarrow(!inline && window.innerWidth >= 768 && window.innerWidth < 1024); };
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    // ── Active image (for prev/next navigation) ──────────────────────────────
    const [activeImageId, setActiveImageId] = useState(imageId);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        if (opened) {
            setActiveImageId(imageId);
        }
    }, [opened, imageId]);

    useEffect(() => {
        const idx = images.findIndex(img => String(img.id) === String(activeImageId));
        setActiveIndex(idx >= 0 ? idx : 0);
    }, [activeImageId, images]);

    // Background of the currently active image (updates on navigation). May be a
    // solid colour, or a special token: gradient:/pattern:/preset:.
    const activeBgKey = images[activeIndex]?.background ?? bgKey;

    // Preset-backdrop image loader — loads the /images/bg-presets/<id>.webp so it
    // can be painted onto the canvas. Redraw is triggered via bgPresetLoaded.
    const bgPresetImgRef = useRef(null);
    const [bgPresetLoaded, setBgPresetLoaded] = useState(0);
    useEffect(() => {
        const d = parseBgToken(activeBgKey);
        if (d.kind !== 'preset') { bgPresetImgRef.current = null; return; }
        let cancelled = false;
        let objUrl = null;
        // Preset URLs are also shown as CSS backgrounds (no CORS), which caches a
        // response without CORS headers. Loading the bare URL with crossOrigin then
        // fails silently and the export falls back to white. Re-fetch in CORS mode
        // and load from a same-origin blob URL so the canvas isn't tainted.
        (async () => {
            try {
                const resp = await fetch(presetSrc(d.id), { mode: 'cors', cache: 'reload' });
                if (!resp.ok) throw new Error(`bg fetch failed: ${resp.status}`);
                objUrl = URL.createObjectURL(await resp.blob());
                const im = new Image();
                im.onload = () => { if (!cancelled) { bgPresetImgRef.current = im; setBgPresetLoaded(n => n + 1); } };
                im.src = objUrl;
            } catch { /* leave ref null — fillBg keeps white */ }
        })();
        return () => { cancelled = true; if (objUrl) setTimeout(() => URL.revokeObjectURL(objUrl), 5000); };
    }, [activeBgKey]);

    // Paint the active background (solid colour, gradient, pattern, or preset
    // image) onto a canvas rect. `fallbackWhite` forces an opaque white fill when
    // there's no resolvable background (used for JPEG export so it isn't black).
    const fillBg = useCallback((ctx, w, h, fallbackWhite = false) => {
        const d = parseBgToken(activeBgKey);
        if (d.kind === 'gradient' || d.kind === 'pattern') {
            if (paintSpecialBg(ctx, 0, 0, w, h, activeBgKey)) return;
        } else if (d.kind === 'preset') {
            const bi = bgPresetImgRef.current;
            if (bi && bi.complete && bi.naturalWidth) { drawImageCover(ctx, bi, 0, 0, w, h); return; }
            ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h); return; // not loaded yet
        }
        const solid = resolveBgColor(activeBgKey);
        if (solid) { ctx.fillStyle = solid; ctx.fillRect(0, 0, w, h); }
        else if (fallbackWhite) { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h); }
    }, [activeBgKey]);

    const navigate = (delta) => {
        const nextIdx = activeIndex + delta;
        if (nextIdx < 0 || nextIdx >= images.length) return;
        setActiveImageId(images[nextIdx].id);
    };

    const hasPrev = activeIndex > 0;
    const hasNext = activeIndex < images.length - 1;

    // ── Blob image loading ───────────────────────────────────────────────────
    const [blobUrl, setBlobUrl] = useState(null);
    const [imageLoading, setImageLoading] = useState(true);
    const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
    const [isDualImage, setIsDualImage] = useState(false);
    const imgRef = useRef(null);

    useEffect(() => {
        if (!opened || !activeImageId) return;
        let cancelled = false;
        setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
        setImageLoading(true);

        (getBlob ? getBlob(activeImageId) : imageGenerationService.downloadImage(activeImageId))
            .then(blob => {
                if (cancelled) return;
                setBlobUrl(URL.createObjectURL(blob));
            })
            .catch(err => {
                console.error('Failed to load preview blob:', err);
                if (!cancelled) setImageLoading(false);
            });

        return () => { cancelled = true; };
    }, [opened, activeImageId]);

    useEffect(() => {
        return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
    }, [blobUrl]);

    // ── Image edits: rotate / mirror / zoom (the crop box lives on the canvas) ──
    // Source composition: fetched blob → rotate+mirror BAKED into workingSrc (a CSS
    // transform would break the crop math) → the crop box (percent of that image) and
    // zoom (a CSS scale about the centre, like the crop modal). The export replays the
    // same steps on the full-size image, so what you see is what downloads.
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);   // 0 | 90 | 180 | 270
    const [mirror, setMirror] = useState(false);   // horizontal flip
    const [workingSrc, setWorkingSrc] = useState(null); // base baked with rotation+mirror
    const workUrlRef = useRef(null);

    const resetEdits = useCallback(() => {
        setZoom(1); setRotation(0); setMirror(false);
        if (workUrlRef.current) { URL.revokeObjectURL(workUrlRef.current); workUrlRef.current = null; }
        setWorkingSrc(null);
    }, []);

    // New image / reopen → drop all edits.
    useEffect(() => { resetEdits(); }, [opened, activeImageId, resetEdits]);

    // Bake rotation+mirror into the working image whenever they change.
    useEffect(() => {
        const base = blobUrl;
        if (!base) return;
        if (rotation === 0 && !mirror) {
            if (workUrlRef.current) { URL.revokeObjectURL(workUrlRef.current); workUrlRef.current = null; }
            setWorkingSrc(null);
            return;
        }
        let cancelled = false;
        const im = new Image();
        im.onload = () => {
            if (cancelled) return;
            const w = im.naturalWidth, h = im.naturalHeight;
            const swap = rotation === 90 || rotation === 270;
            const c = document.createElement('canvas');
            c.width = swap ? h : w;
            c.height = swap ? w : h;
            const cx = c.getContext('2d');
            cx.imageSmoothingQuality = 'high';
            cx.translate(c.width / 2, c.height / 2);
            cx.rotate((rotation * Math.PI) / 180);
            if (mirror) cx.scale(-1, 1);
            cx.drawImage(im, -w / 2, -h / 2, w, h);
            c.toBlob((b) => {
                if (!b || cancelled) return;
                if (workUrlRef.current) URL.revokeObjectURL(workUrlRef.current);
                const u = URL.createObjectURL(b);
                workUrlRef.current = u;
                setWorkingSrc(u);
            }, 'image/png');
        };
        im.src = base;
        return () => { cancelled = true; };
    }, [blobUrl, rotation, mirror]);

    useEffect(() => () => { if (workUrlRef.current) URL.revokeObjectURL(workUrlRef.current); }, []);

    // ── Visa dropdown positioning ─────────────────────────────────────────────
    const visaButtonRef = useRef(null);
    const rightPanelRef = useRef(null);
    const [visaDropdownPos, setVisaDropdownPos] = useState(null);

    const handleVisaClick = () => {
        if (!visaExpanded && visaButtonRef.current && rightPanelRef.current) {
            const btnRect = visaButtonRef.current.getBoundingClientRect();
            const panelRect = rightPanelRef.current.getBoundingClientRect();
            setVisaDropdownPos({
                top: btnRect.bottom - panelRect.top + 6,
                left: Math.min(btnRect.left - panelRect.left, panelRect.width - 296),
            });
        }
        setVisaExpanded(v => !v);
    };

    // ── Canvas & crop box ────────────────────────────────────────────────────
    // The photo is drawn (with its saved background) on a canvas sized to fill the
    // canvas area — full height, width following the photo; the crop box (percent of
    // the photo) sits on it at all times.
    const canvasRef = useRef(null);
    const areaRef = useRef(null);
    const [areaSize, setAreaSize] = useState({ w: 0, h: 0 });
    const [crop, setCrop] = useState(null); // react-image-crop percent crop

    // ── Settings ─────────────────────────────────────────────────────────────
    const [selectedPreset, setSelectedPreset] = useState(null);
    const [selectedVisaGroup, setSelectedVisaGroup] = useState(null);
    const [visaExpanded, setVisaExpanded] = useState(false);
    const [customExpanded, setCustomExpanded] = useState(false);
    const [customWidth, setCustomWidth] = useState(800);
    const [customHeight, setCustomHeight] = useState(800);
    const [unit, setUnit] = useState('px');
    const [cropRatio, setCropRatio] = useState(null);
    const [imageQuality, setImageQuality] = useState(90);
    const [downloading, setDownloading] = useState(false);

    // ── Border ───────────────────────────────────────────────────────────────
    // When enabled, the photo is inset inside a solid colour frame while the export
    // dimensions stay fixed. `borderPct` is the frame thickness as a percentage of the
    // final image's shorter side, so it stays consistent across every export size.
    const [borderEnabled, setBorderEnabled] = useState(false);
    const [borderColor, setBorderColor] = useState('#ffffff');
    const [borderPct, setBorderPct] = useState(4);

    // Thickness in pixels of the inner rectangle `iw`×`ih` (its shorter side).
    const borderPxFor = (w, h) => (borderEnabled ? Math.round((borderPct / 100) * Math.min(w, h)) : 0);

    // Reset settings when modal re-opens
    useEffect(() => {
        if (opened) {
            setSelectedPreset(null);
            setSelectedVisaGroup(null);
            setVisaExpanded(false);
            setCustomExpanded(false);
            setCropRatio(null);
            setCrop(null);
            setBorderEnabled(false);
        }
    }, [opened]);

    // ── Display size: the photo fits the canvas area (full height, width auto; may scale up) ──
    useLayoutEffect(() => {
        const el = areaRef.current;
        if (!el || !opened) return undefined;
        const measure = () => {
            const cs = getComputedStyle(el);
            setAreaSize({
                w: Math.max(0, el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight)),
                h: Math.max(0, el.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom)),
            });
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [opened, imageLoading]);
    const fitScale = naturalSize.w && naturalSize.h && areaSize.w && areaSize.h
        ? Math.min(areaSize.w / naturalSize.w, areaSize.h / naturalSize.h) : 0;
    const dispW = Math.max(1, Math.floor(naturalSize.w * fitScale));
    const dispH = Math.max(1, Math.floor(naturalSize.h * fitScale));

    // Paint the photo over its saved background (solid / gradient / pattern / preset) at screen resolution.
    const drawCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const img = imgRef.current;
        if (!canvas || !img || !img.complete || img.naturalWidth === 0 || !fitScale) return;
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const cw = Math.max(1, Math.round(dispW * dpr));
        const ch = Math.max(1, Math.round(dispH * dpr));
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        fillBg(ctx, cw, ch);
        ctx.drawImage(img, 0, 0, cw, ch);
    }, [fitScale, dispW, dispH, fillBg, bgPresetLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!imageLoading) drawCanvas();
    }, [drawCanvas, imageLoading]);

    const handleImageLoad = useCallback(() => {
        const img = imgRef.current;
        if (!img) return;
        setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
        setIsDualImage(img.naturalWidth / img.naturalHeight > 1.5);
        setCustomWidth(img.naturalWidth);
        setCustomHeight(img.naturalHeight);
        setImageLoading(false);
    }, []);

    // Mouse-wheel zoom over the canvas area (the crop modal's): up = in, down = out.
    useEffect(() => {
        const el = areaRef.current;
        if (!el || !opened) return undefined;
        const onWheel = (e) => {
            e.preventDefault();
            const step = e.deltaY < 0 ? 0.05 : -0.05;
            setZoom((z) => Math.min(3, Math.max(1, +(z + step).toFixed(2))));
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [opened, imageLoading]);

    // Unit helpers + export size: see below. The crop box's shape follows the chosen size:
    // a preset / visa size locks it to that size's ratio, a ratio chip to the chip, "free" leaves it free.
    // ── Unit helpers ─────────────────────────────────────────────────────────
    const toUnit = (px, u = unit) => {
        if (u === 'px') return px;
        return parseFloat((px / UNIT_TO_PX[u]).toFixed(2));
    };
    const toPx = (val, u = unit) => Math.round(val * UNIT_TO_PX[u]);

    // ── Preset selection ─────────────────────────────────────────────────────
    const handlePresetSelect = (preset) => {
        setSelectedPreset(preset.key);
        setCustomExpanded(false);
        if (preset.exportW && preset.exportH) setCropRatio(preset.exportW / preset.exportH);
    };

    const handleVisaGroupSelect = (group) => {
        setSelectedVisaGroup(group.key);
        setSelectedPreset('visa');
        const dims = parseVisaSize(group);
        if (dims) setCropRatio(dims.w / dims.h);
    };

    const getExportDims = () => {
        if (selectedPreset === 'visa' && selectedVisaGroup) {
            const group = VISA_GROUPS.find(g => g.key === selectedVisaGroup);
            const dims = group ? parseVisaSize(group) : null;
            if (dims) return {
                exportW: Math.round(dims.w * MM_TO_PX),
                exportH: Math.round(dims.h * MM_TO_PX),
            };
        }
        if (selectedPreset && selectedPreset !== 'visa' && selectedPreset !== 'custom') {
            const preset = PHOTO_SIZES.find(p => p.key === selectedPreset);
            if (preset?.exportW) return { exportW: preset.exportW, exportH: preset.exportH };
        }
        return { exportW: customWidth, exportH: customHeight };
    };

    // The crop box's ratio: the chosen size's (preset / visa / custom W×H), else the ratio chip (null = free).
    const presetAspect = (() => {
        if (!selectedPreset) return null;
        if (selectedPreset === 'custom') return cropRatio ?? (customWidth && customHeight ? customWidth / customHeight : null);
        const d = getExportDims();
        return d.exportW && d.exportH ? d.exportW / d.exportH : null;
    })();
    const aspect = presetAspect ?? cropRatio; // null = free

    // A box of `asp` that keeps the current box's centre and (about) its area, inside the photo — so changing the size only
    // reshapes the crop; the photo itself never moves.
    const reshapeCrop = useCallback((prev, asp) => {
        if (!dispW || !dispH) return prev;
        if (!prev) {
            return asp
                ? centerCrop(makeAspectCrop({ unit: '%', width: 100 }, asp, dispW, dispH), dispW, dispH)
                : { unit: '%', x: 0, y: 0, width: 100, height: 100 };
        }
        if (!asp) return prev;
        const pw = (prev.width / 100) * dispW, ph = (prev.height / 100) * dispH;
        const cx = (prev.x / 100) * dispW + pw / 2, cy = (prev.y / 100) * dispH + ph / 2;
        let w = Math.sqrt(pw * ph * asp), h = w / asp;
        const fit = Math.min(1, dispW / w, dispH / h);
        w *= fit; h *= fit;
        const x = Math.min(Math.max(0, cx - w / 2), dispW - w);
        const y = Math.min(Math.max(0, cy - h / 2), dispH - h);
        return { unit: '%', x: (x / dispW) * 100, y: (y / dispH) * 100, width: (w / dispW) * 100, height: (h / dispH) * 100 };
    }, [dispW, dispH]);

    // Seed the box once the photo is measured; reshape it when the ratio changes; re-seed on a new photo / rotation.
    const seededForRef = useRef(null);
    useEffect(() => {
        if (imageLoading || !fitScale) return;
        const key = `${activeImageId}|${naturalSize.w}x${naturalSize.h}`;
        if (seededForRef.current !== key) {
            seededForRef.current = key;
            setCrop(reshapeCrop(null, aspect));
            return;
        }
        setCrop((prev) => reshapeCrop(prev, aspect));
    }, [aspect, imageLoading, fitScale, activeImageId, naturalSize.w, naturalSize.h]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (!opened) seededForRef.current = null; }, [opened]);

    // The crop in the working image's natural pixels, with the zoom (a scale about the centre) folded in.
    const cropSourceRect = (W = naturalSize.w, H = naturalSize.h) => {
        const c = crop || { x: 0, y: 0, width: 100, height: 100 };
        const x = (c.x / 100) * W, y = (c.y / 100) * H, w = (c.width / 100) * W, h = (c.height / 100) * H;
        const sw = Math.max(1, w / zoom), sh = Math.max(1, h / zoom);
        const sx = Math.min(Math.max(0, W / 2 + (x - W / 2) / zoom), W - sw);
        const sy = Math.min(Math.max(0, H / 2 + (y - H / 2) / zoom), H - sh);
        return { sx, sy, sw, sh };
    };

    // Output size: a preset's 300-DPI anchor (upgraded towards the crop's own pixels, see upgradeExportDims), a custom W×H
    // (height from the ratio chip when one is on), or — with no size chosen — the crop's own pixels.
    const outputDims = (W = naturalSize.w, H = naturalSize.h) => {
        const { sw, sh } = cropSourceRect(W, H);
        if (!selectedPreset) return { exportW: Math.max(1, Math.round(sw)), exportH: Math.max(1, Math.round(sh)) };
        if (selectedPreset === 'custom') {
            return cropRatio ? { exportW: customWidth, exportH: Math.max(1, Math.round(customWidth / cropRatio)) } : { exportW: customWidth, exportH: customHeight };
        }
        const base = getExportDims();
        return upgradeExportDims(base.exportW, base.exportH, sw);
    };

    // ── Download ─────────────────────────────────────────────────────────────
    const handleDownload = async () => {
        if (!activeImageId) return;
        setDownloading(true);
        try {
            // Export from the same composed source the preview shows: the full-size photo →
            // rotation+mirror bake → the crop box (+ zoom) → the output size.
            const blob = await (getBlob ? getBlob(activeImageId) : imageGenerationService.downloadImage(activeImageId));
            const srcUrl = URL.createObjectURL(blob);
            const rawImg = new Image();
            await new Promise((res, rej) => { rawImg.onload = res; rawImg.onerror = rej; rawImg.src = srcUrl; });

            // Bake rotation + mirror (drawImage accepts a canvas as source).
            let img = rawImg;
            if (rotation !== 0 || mirror) {
                const w = rawImg.naturalWidth, h = rawImg.naturalHeight;
                const swap = rotation === 90 || rotation === 270;
                const bc = document.createElement('canvas');
                bc.width = swap ? h : w;
                bc.height = swap ? w : h;
                const bctx = bc.getContext('2d');
                bctx.imageSmoothingQuality = 'high';
                bctx.translate(bc.width / 2, bc.height / 2);
                bctx.rotate((rotation * Math.PI) / 180);
                if (mirror) bctx.scale(-1, 1);
                bctx.drawImage(rawImg, -w / 2, -h / 2, w, h);
                img = bc;
            }
            const W = img.width, H = img.height;
            const { sx, sy, sw, sh } = cropSourceRect(W, H);
            const { exportW: finalW, exportH: finalH } = outputDims(W, H);

            const fullCrop = sx < 1 && sy < 1 && sw >= W - 1 && sh >= H - 1;
            const isSameSize = fullCrop && finalW === W && finalH === H && !borderEnabled && rotation === 0 && !mirror;

            let downloadUrl;
            if (isSameSize) {
                downloadUrl = srcUrl;
            } else {
                const canvas = document.createElement('canvas');
                canvas.width = finalW;
                canvas.height = finalH;
                const ctx = canvas.getContext('2d');
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';

                // Border frame (no-op when disabled: b=0 → inner region == full canvas).
                const b = borderPxFor(finalW, finalH);
                const ix = b, iy = b, iw = finalW - 2 * b, ih = finalH - 2 * b;
                if (b > 0) { ctx.fillStyle = borderColor; ctx.fillRect(0, 0, finalW, finalH); }
                // Paint the saved background (solid / gradient / pattern / preset image)
                // into the inner region; fall back to white for JPEG.
                ctx.save();
                ctx.translate(ix, iy);
                fillBg(ctx, iw, ih, true);
                ctx.restore();

                ctx.drawImage(img, sx, sy, sw, sh, ix, iy, iw, ih);

                const quality = Math.max(0.85, imageQuality / 100);
                const outBlob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', quality));
                URL.revokeObjectURL(srcUrl);
                downloadUrl = URL.createObjectURL(outBlob);
            }

            const link = document.createElement('a');
            link.href = downloadUrl;
            const uuid = (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2, 10));
            link.download = `sohozkaj-${uuid}_${finalW}x${finalH}.jpg`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(downloadUrl);

            notifications.show({
                message: t('imageGeneration.notifications.customDownloadSuccess', {
                    width: finalW, height: finalH, quality: imageQuality,
                }),
                color: 'green',
            });

            imageGenerationService.trackDownload(activeImageId).catch(console.error);
            // Keep modal open intentionally
        } catch (err) {
            console.error('Download error:', err);
            notifications.show({
                message: t('imageGeneration.notifications.downloadFailed'),
                color: 'red',
            });
        } finally {
            setDownloading(false);
        }
    };

    // Preset export sizes are 300-DPI print anchors (passport = 532×650). When the
    // crop has more native pixels, export at up to 2× the anchor — capped at the crop
    // so we never upscale — so a 1K+ image keeps its resolution instead of being downsized.
    const upgradeExportDims = (baseW, baseH, cropW) => {
        if (!baseW || !baseH || !cropW) return { exportW: baseW, exportH: baseH };
        const s = Math.min(2, Math.max(1, cropW / baseW));
        return { exportW: Math.round(baseW * s), exportH: Math.round(baseH * s) };
    };

    // ── Derived values ───────────────────────────────────────────────────────
    const { exportW, exportH } = naturalSize.w ? outputDims() : { exportW: 0, exportH: 0 };
    const estKb = Math.round((exportW * exportH * (imageQuality / 100) * 3) / 1024 / 10);
    // the border, previewed inside the crop box (same share of its shorter side as the export)
    const borderPreviewPx = crop && borderEnabled
        ? Math.round((borderPct / 100) * Math.min((crop.width / 100) * dispW, (crop.height / 100) * dispH)) : 0;

    const visibleSizes = PHOTO_SIZES.filter(p => {
        if (p.isDual) return isDualImage;
        if (p.singleOnly) return !isDualImage;
        return true;
    });

    // ── Render ───────────────────────────────────────────────────────────────
    const body = (
            <>
                {/* Hidden img for blob loading */}
                {blobUrl && (
                    <img
                        ref={imgRef}
                        src={workingSrc || blobUrl}
                        alt=""
                        className="hidden"
                        onLoad={handleImageLoad}
                    />
                )}

                {/* Shimmer keyframes */}
                <style>{`
                    @keyframes shimmer {
                        0% { transform: translateX(-100%); }
                        100% { transform: translateX(100%); }
                    }
                    .sk-dl-crop .ReactCrop__drag-handle::after {
                        width: 14px; height: 14px; border-radius: 50%;
                        background: #E68A00; border: 2px solid #fff;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.4);
                        transform: translate(-2px, -2px);
                    }
                    .sk-dl-crop .ReactCrop__crop-selection {
                        border-radius: 4px;
                        box-shadow: inset 0 0 0 ${borderPreviewPx}px ${borderPreviewPx ? borderColor : 'transparent'}, 0 0 0 9999em rgba(0, 0, 0, 0.6);
                    }
                `}</style>

                {/* Shared editor-modal DESIGN shell (same as the crop modal); the download
                    functionality stays in this component and is passed in dynamically. */}
                <EditorModalShell
                    isMobile={isMobile}
                    dragHandle={!inline}
                    bare={inline}
                    icon={<IconDownload size={20} />}
                    title={t('imageGeneration.customDownloadSettings')}
                    headerExtra={inline ? null : <>
                        {images.length > 1 && (
                            <span className="text-xs text-zinc-400 tabular-nums shrink-0">
                                {activeIndex + 1} / {images.length}
                            </span>
                        )}
                        {/* Image details — moved from the old stats bar into the header */}
                        <div className="hidden sm:flex items-center gap-5 min-w-0 ml-3 overflow-hidden">
                            <span className="text-xs text-zinc-400 whitespace-nowrap">
                                {t('imageGeneration.dimensions')}: <span className="font-semibold text-zinc-600 dark:text-zinc-300">{imageLoading ? '—' : `${exportW} × ${exportH} px`}</span>
                            </span>
                            <span className="text-xs text-zinc-400 whitespace-nowrap">
                                {t('imageGeneration.originalSize')}: <span className="font-semibold text-zinc-600 dark:text-zinc-300">{imageLoading ? '—' : `${naturalSize.w} × ${naturalSize.h} px`}</span>
                            </span>
                            <span className="text-xs text-zinc-400 whitespace-nowrap">
                                {t('imageGeneration.estimatedSize')}: <span className="font-semibold text-zinc-600 dark:text-zinc-300">{imageLoading ? '—' : `~${estKb} KB`}</span>
                            </span>
                        </div>
                    </>}
                    onClose={onClose}
                    canvasRef={areaRef}
                    sidebarWidth={narrow ? 296 : 400}
                    sidebarDock
                    canvas={
                        <div className="group w-full h-full flex items-center justify-center relative overflow-hidden min-h-0">
                            {/* Floating prev/next — visible only on hover */}
                            {images.length > 1 && hasPrev && (
                                <button
                                    onClick={() => navigate(-1)}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-zinc-800/80 hover:bg-zinc-700 border border-zinc-600 text-white flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                >
                                    <IconChevronLeft size={18} />
                                </button>
                            )}
                            {images.length > 1 && hasNext && (
                                <button
                                    onClick={() => navigate(1)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-zinc-800/80 hover:bg-zinc-700 border border-zinc-600 text-white flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                >
                                    <IconChevronRight size={18} />
                                </button>
                            )}
                            {imageLoading && (
                                <div className="rounded-xl overflow-hidden bg-zinc-200 dark:bg-zinc-700 relative" style={{ aspectRatio: '1 / 1', height: '90%', maxHeight: '100%', maxWidth: '100%' }}>
                                    <div
                                        className="absolute inset-0"
                                        style={{
                                            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%)',
                                            animation: 'shimmer 1.4s infinite',
                                        }}
                                    />
                                </div>
                            )}
                            {/* The photo at the canvas area's full height, the crop box always on it (drag it, or its corners) */}
                            {!imageLoading && fitScale > 0 && (
                                <ReactCrop
                                    className="sk-dl-crop"
                                    crop={crop || undefined}
                                    aspect={aspect || undefined}
                                    keepSelection
                                    ruleOfThirds
                                    minWidth={30}
                                    onChange={(_px, pct) => setCrop(pct)}
                                >
                                    <canvas
                                        ref={canvasRef}
                                        className="block select-none"
                                        style={{ width: dispW, height: dispH, transform: `scale(${zoom})`, transition: 'transform 0.15s ease' }}
                                    />
                                </ReactCrop>
                            )}
                        </div>
                    }
                    sidebar={isMobile ? (
                    <DownloadOptionsDock
                        sizes={visibleSizes}
                        sizeIcons={SIZE_ICONS}
                        selectedPreset={selectedPreset}
                        onPreset={handlePresetSelect}
                        visaExpanded={visaExpanded}
                        onVisaToggle={() => setVisaExpanded((v) => !v)}
                        visaGroups={VISA_GROUPS}
                        selectedVisaGroup={selectedVisaGroup}
                        onVisaGroup={(group) => { handleVisaGroupSelect(group); setVisaExpanded(false); }}
                        customExpanded={customExpanded}
                        onCustomToggle={() => {
                            const next = !customExpanded;
                            setCustomExpanded(next);
                            setSelectedPreset(next ? 'custom' : null);
                        }}
                        customWidth={customWidth}
                        customHeight={customHeight}
                        onCustomWidth={(v) => setCustomWidth(toPx(v))}
                        onCustomHeight={(v) => setCustomHeight(toPx(v))}
                        unit={unit}
                        onUnit={setUnit}
                        toUnit={toUnit}
                        ratios={CROP_RATIOS}
                        cropRatio={cropRatio}
                        // the tab is always there on a phone: a ratio replaces a chosen size
                        onRatio={(value) => { setSelectedPreset(null); setCustomExpanded(false); setCropRatio(value); }}
                        qualities={QUALITY_PRESETS}
                        imageQuality={imageQuality}
                        onQuality={setImageQuality}
                        borderEnabled={borderEnabled}
                        onBorderEnabled={setBorderEnabled}
                        borderColor={borderColor}
                        onBorderColor={setBorderColor}
                        swatches={BORDER_SWATCHES}
                        borderPct={borderPct}
                        onBorderPct={setBorderPct}
                        borderPx={borderPxFor(exportW, exportH)}
                        onDownload={handleDownload}
                        downloading={downloading}
                    />
                    ) : (
                    /* RIGHT: Settings — the shell provides the sidebar frame */
                    <div ref={rightPanelRef} className="flex flex-col flex-1 min-h-0 relative overflow-visible">
                        <div className={`flex-1 overflow-y-auto ${narrow ? 'p-3 space-y-3.5' : 'p-4 space-y-5'}`}>

                            {/* Photo size presets */}
                            <div>
                                <p className="font-semibold text-zinc-400 dark:text-zinc-500 uppercase tracking-wide text-xs mb-2">
                                    {t('imageGeneration.photoSizes')}
                                </p>
                                <div className={narrow ? 'grid grid-cols-4 gap-x-1.5 gap-y-2' : 'grid grid-cols-5 gap-2'}>
                                    {visibleSizes.map(preset => {
                                        const Icon = SIZE_ICONS[preset.key] ?? IconPhoto;
                                        const isSelected = selectedPreset === preset.key;
                                        const btnSize = narrow ? 'w-12 h-12' : 'w-14 h-14';
                                        const iconSize = narrow ? 22 : 26;

                                        if (preset.isVisa) {
                                            return (
                                                <button
                                                    key="visa"
                                                    ref={visaButtonRef}
                                                    onClick={handleVisaClick}
                                                    className="flex flex-col items-center gap-1 cursor-pointer shrink-0 w-full"
                                                >
                                                    <div className={`${btnSize} rounded-xl border-2 flex items-center justify-center transition-colors relative ${isSelected ? 'border-primary bg-primary/5 dark:bg-primary/10' : 'border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-600'}`}>
                                                        <Icon size={iconSize} className={isSelected ? 'text-primary' : 'text-zinc-400 dark:text-zinc-500'} />
                                                        <span className="absolute -bottom-0.5 -right-0.5">
                                                            {visaExpanded
                                                                ? <IconChevronUp size={11} className={`${isSelected ? 'text-primary' : 'text-zinc-400'} bg-white dark:bg-zinc-900 rounded-full`} />
                                                                : <IconChevronDown size={11} className={`${isSelected ? 'text-primary' : 'text-zinc-400'} bg-white dark:bg-zinc-900 rounded-full`} />
                                                            }
                                                        </span>
                                                    </div>
                                                    <span className={`text-[10px] font-medium leading-none ${isSelected ? 'text-primary' : 'text-zinc-500 dark:text-zinc-400'}`}>
                                                        {t('imageGeneration.abbrVisa')}
                                                    </span>
                                                </button>
                                            );
                                        }

                                        return (
                                            <button
                                                key={preset.key}
                                                onClick={() => handlePresetSelect(preset)}
                                                className="flex flex-col items-center gap-1 cursor-pointer shrink-0 w-full"
                                            >
                                                <div className={`${btnSize} rounded-xl border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-primary bg-primary/5 dark:bg-primary/10' : 'border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-600'}`}>
                                                    <Icon size={iconSize} className={isSelected ? 'text-primary' : 'text-zinc-400 dark:text-zinc-500'} />
                                                </div>
                                                <span className={`text-[10px] font-medium leading-none ${isSelected ? 'text-primary' : 'text-zinc-500 dark:text-zinc-400'}`}>
                                                    {t(`imageGeneration.${preset.labelKey}`)}
                                                </span>
                                            </button>
                                        );
                                    })}

                                    {/* Custom expander card */}
                                    <button
                                        onClick={() => {
                                            const next = !customExpanded;
                                            setCustomExpanded(next);
                                            setSelectedPreset(next ? 'custom' : null);
                                        }}
                                        className="flex flex-col items-center gap-1 cursor-pointer shrink-0 w-full"
                                    >
                                        <div className={`${narrow ? 'w-12 h-12' : 'w-14 h-14'} rounded-xl border-2 flex items-center justify-center transition-colors ${selectedPreset === 'custom' ? 'border-primary bg-primary/5 dark:bg-primary/10' : 'border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-600'}`}>
                                            <IconPlus size={narrow ? 22 : 26} className={selectedPreset === 'custom' ? 'text-primary' : 'text-zinc-400 dark:text-zinc-500'} />
                                        </div>
                                        <span className={`text-[11px] font-medium leading-none ${selectedPreset === 'custom' ? 'text-primary' : 'text-zinc-500 dark:text-zinc-400'}`}>
                                            {t('imageGeneration.abbrCustom') || 'Custom'}
                                        </span>
                                    </button>
                                </div>

                                {/* Custom size inputs */}
                                {customExpanded && (
                                    <div className="mt-2 p-2.5 bg-zinc-50 dark:bg-zinc-800/50 rounded-xl border border-zinc-200 dark:border-zinc-700 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
                                                {t('imageGeneration.dimensions')}
                                            </span>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <button className="px-2 py-0.5 text-xs border border-zinc-200 dark:border-zinc-700 rounded-md bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 flex items-center gap-1 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors">
                                                        {unit}
                                                        <IconChevronDown size={10} />
                                                    </button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="min-w-[70px]">
                                                    {['px', 'mm', 'cm', 'in'].map(u => (
                                                        <DropdownMenuItem
                                                            key={u}
                                                            className="cursor-pointer text-xs"
                                                            onClick={() => setUnit(u)}
                                                        >
                                                            {u}
                                                        </DropdownMenuItem>
                                                    ))}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                        <div className="flex gap-2">
                                            <div className="flex-1">
                                                <label className="text-xs text-zinc-500 dark:text-zinc-400 mb-0.5 block">
                                                    {t('imageGeneration.widthPx').replace(/\s*\(px\)/i, '')} ({unit})
                                                </label>
                                                <input
                                                    type="number"
                                                    value={toUnit(customWidth)}
                                                    onChange={e => setCustomWidth(toPx(Number(e.target.value)))}
                                                    className="w-full px-2 py-1.5 text-xs border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <label className="text-xs text-zinc-500 dark:text-zinc-400 mb-0.5 block">
                                                    {t('imageGeneration.heightPx').replace(/\s*\(px\)/i, '')} ({unit})
                                                </label>
                                                <input
                                                    type="number"
                                                    value={toUnit(customHeight)}
                                                    onChange={e => setCustomHeight(toPx(Number(e.target.value)))}
                                                    className="w-full px-2 py-1.5 text-xs border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Crop Ratio — only when no preset or custom is active */}
                            {(!selectedPreset || selectedPreset === 'custom') && (
                                <div>
                                    <div className="flex items-center gap-1 mb-2">
                                        <IconCrop size={12} className="text-zinc-400" />
                                        <p className="text-[10px] font-semibold text-zinc-400 dark:text-zinc-500 uppercase tracking-wide">
                                            {t('imageGeneration.cropRatio')}
                                        </p>
                                    </div>
                                    <div className={`grid gap-1 ${narrow ? 'grid-cols-4' : 'grid-cols-8'}`}>
                                        {CROP_RATIOS.map(r => (
                                            <button
                                                key={r.labelKey ?? r.label}
                                                onClick={() => setCropRatio(r.value)}
                                                className={`py-1 text-[10px] rounded-lg border transition-colors text-center ${cropRatio === r.value ? 'bg-primary text-white border-primary' : 'border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'}`}
                                            >
                                                {r.labelKey ? t(`imageGeneration.${r.labelKey}`) : r.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Quality */}
                            <div>
                                <p className="font-semibold text-zinc-400 dark:text-zinc-500 uppercase tracking-wide text-xs mb-2">
                                    {t('imageGeneration.imageQuality')}
                                </p>
                                <div className="flex gap-1 mb-2">
                                    {QUALITY_PRESETS.map(q => (
                                        <button
                                            key={q.key}
                                            onClick={() => setImageQuality(q.value)}
                                            className={`flex-1 text-xs rounded-lg border font-medium transition-colors py-1.5 ${imageQuality === q.value ? 'bg-primary text-white border-primary' : 'border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'}`}
                                        >
                                            {t(`imageGeneration.${q.key === 'low' ? 'qualityLow' : q.key === 'med' ? 'qualityMed' : 'qualityHigh'}`)}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex items-center gap-2">
                                        <input
                                            type="range"
                                            value={imageQuality}
                                            onChange={e => setImageQuality(Number(e.target.value))}
                                            min={50}
                                            max={100}
                                            step={1}
                                            className="flex-1 accent-primary"
                                            style={{ height: 4 }}
                                        />
                                        <span className="text-[10px] font-mono text-zinc-500 dark:text-zinc-400 w-7 text-right shrink-0">
                                            {imageQuality}%
                                        </span>
                                </div>
                            </div>

                            {/* Border */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-1">
                                        <IconBorderOuter size={12} className="text-zinc-400" />
                                        <p className="text-[10px] font-semibold text-zinc-400 dark:text-zinc-500 uppercase tracking-wide">
                                            {t('imageGeneration.border')}
                                        </p>
                                    </div>
                                    {/* Enable toggle */}
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={borderEnabled}
                                        onClick={() => setBorderEnabled(v => !v)}
                                        className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${borderEnabled ? 'bg-primary' : 'bg-zinc-300 dark:bg-zinc-600'}`}
                                    >
                                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${borderEnabled ? 'translate-x-4' : ''}`} />
                                    </button>
                                </div>

                                {borderEnabled && (
                                    <div className="space-y-2.5">
                                        {/* Colour swatches + custom picker */}
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            {BORDER_SWATCHES.map(c => (
                                                <button
                                                    key={c}
                                                    type="button"
                                                    onClick={() => setBorderColor(c)}
                                                    title={c}
                                                    className={`w-6 h-6 rounded-full border transition-transform hover:scale-110 ${borderColor.toLowerCase() === c.toLowerCase() ? 'ring-2 ring-primary ring-offset-1 dark:ring-offset-zinc-900 border-transparent' : 'border-zinc-300 dark:border-zinc-600'}`}
                                                    style={{ backgroundColor: c }}
                                                />
                                            ))}
                                            <label
                                                className="w-6 h-6 rounded-full border border-dashed border-zinc-300 dark:border-zinc-600 flex items-center justify-center cursor-pointer overflow-hidden relative"
                                                title={t('imageGeneration.borderColor')}
                                                style={{ backgroundColor: BORDER_SWATCHES.includes(borderColor.toLowerCase()) ? undefined : borderColor }}
                                            >
                                                {BORDER_SWATCHES.includes(borderColor.toLowerCase()) && <IconPlus size={12} className="text-zinc-400" />}
                                                <input
                                                    type="color"
                                                    value={borderColor}
                                                    onChange={e => setBorderColor(e.target.value)}
                                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                                />
                                            </label>
                                        </div>

                                        {/* Thickness slider */}
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] text-zinc-500 dark:text-zinc-400 shrink-0">
                                                {t('imageGeneration.borderThickness')}
                                            </span>
                                            <input
                                                type="range"
                                                value={borderPct}
                                                onChange={e => setBorderPct(Number(e.target.value))}
                                                min={0.5}
                                                max={15}
                                                step={0.5}
                                                className="flex-1 accent-primary"
                                                style={{ height: 4 }}
                                            />
                                            <span className="text-[10px] font-mono text-zinc-500 dark:text-zinc-400 w-12 text-right shrink-0">
                                                {borderPxFor(exportW, exportH)}px
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Visa dropdown — absolutely positioned outside overflow-y-auto, inside relative right panel */}
                        {visaExpanded && visaDropdownPos && (
                            <div
                                className="absolute bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-xl p-3 z-[100]"
                                style={{ top: visaDropdownPos.top, left: visaDropdownPos.left, width: 288 }}
                            >
                                <p className="text-[11px] font-semibold text-zinc-400 dark:text-zinc-500 uppercase tracking-wide mb-2">
                                    {t('imageGeneration.abbrVisa')} {t('imageGeneration.photoSizes')}
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {VISA_GROUPS.map(group => (
                                        <button
                                            key={group.key}
                                            onClick={() => { handleVisaGroupSelect(group); setVisaExpanded(false); }}
                                            className={`px-2.5 py-1 text-xs rounded-lg border transition-colors cursor-pointer ${selectedVisaGroup === group.key ? 'bg-primary text-white border-primary' : 'border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800'}`}
                                        >
                                            {group.size}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>
                    )}
                    bottomBar={!isMobile ? (
                        /* Standardized bottom bar — same frame/blocks + tool cluster as the
                           crop modal. No cancel block: the red header X closes the modal. */
                        <EditorBarFrame>
                            <EditorBarTools>
                                <button
                                    style={{ width: 56, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#059669', color: '#fff', border: 'none', borderRadius: 0, cursor: 'pointer' }}
                                    onClick={() => setZoom(z => Math.max(1, +(z - 0.05).toFixed(2)))}
                                    aria-label={t('imageGeneration.cropZoomOut')}
                                >
                                    <IconMinus size={20} strokeWidth={2.5} />
                                </button>
                                <span className="inline-flex items-center justify-center text-[13px] font-bold text-zinc-700 dark:text-zinc-200" style={{ minWidth: 54, padding: '0 6px' }}>{Math.round(zoom * 100)}%</span>
                                <button
                                    style={{ width: 56, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#059669', color: '#fff', border: 'none', borderRadius: 0, cursor: 'pointer' }}
                                    onClick={() => setZoom(z => Math.min(3, +(z + 0.05).toFixed(2)))}
                                    aria-label={t('imageGeneration.cropZoomIn')}
                                >
                                    <IconPlus size={20} strokeWidth={2.5} />
                                </button>
                                <button
                                    className="inline-flex items-center gap-1.5 font-semibold text-sm"
                                    style={{ marginLeft: 12, padding: '0 18px', background: '#059669', color: '#fff', border: 'none', borderRadius: 0, cursor: 'pointer' }}
                                    onClick={() => setRotation(r => (r + 90) % 360)}
                                >
                                    <IconRotateClockwise size={18} /> {t('imageGeneration.cropRotate')}
                                </button>
                                <button
                                    className="inline-flex items-center gap-1.5 font-semibold text-sm"
                                    style={{ marginLeft: 12, padding: '0 18px', background: mirror ? '#E68A00' : '#6366f1', color: '#fff', border: 'none', borderRadius: 0, cursor: 'pointer' }}
                                    onClick={() => setMirror(m => !m)}
                                >
                                    <IconFlipHorizontal size={18} /> {t('imageGeneration.cropMirror')}
                                </button>
                            </EditorBarTools>
                            <EditorBarPrimary onClick={handleDownload} disabled={downloading}>
                                <IconDownload size={18} />
                                {downloading
                                    ? t('imageGeneration.downloading')
                                    : t('imageGeneration.downloadCustom')}
                            </EditorBarPrimary>
                        </EditorBarFrame>
                    ) : null}
                />
            </>
    );
    if (inline) return <div className="flex h-full min-h-0 w-full flex-col">{body}</div>;
    return (
        <Dialog open={opened} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent
                className="p-0 overflow-hidden gap-0 z-[99999] max-w-none w-full rounded-none top-0 bottom-0 translate-y-0 left-0 translate-x-0 border-0 flex flex-col"
                overlayClassName="z-[99998]"
                showClose={false}
                style={{ position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, transform: 'none', height: '100svh' }}
            >
                <DialogTitle className="sr-only">{t('imageGeneration.customDownloadSettings')}</DialogTitle>
                {body}
            </DialogContent>
        </Dialog>
    );
}
